// Interface spec IR → Max Markup, so agents read and edit stored tools in the same form they
// write them. Every attribute is printed in the first form that compiles back to the same
// value, so compileMarkup(printMarkup(spec)) gives the spec again.
import type { MaxInterfaceSpec } from '../interface.ts'
import { printExpr, type NameScope } from './expr.ts'
import { kebab, markupSchema, singular, type Caps } from './schema.ts'
import { ACTION_TAGS, INLINE, INNER_PROPS, TEXT_CONTENT, compileAttribute, pascalToSnake, snakeToPascal } from './compile.ts'

const INDENT = '  '
const LINE = 100

type Obj = Record<string, unknown>

const isObj = (v: unknown): v is Obj => !!v && typeof v === 'object' && !Array.isArray(v)
const isComputedValue = (v: unknown) => isObj(v) && 'expr' in v && Object.keys(v).length === 1

/** Values written as child elements rather than attributes */
function asChild (v: unknown, caps: Caps): boolean {
  if (caps.object && isObj(v) && !(caps.computed && isComputedValue(v))) return true
  if (caps.objects && Array.isArray(v) && v.every(isObj)) return true
  return false
}

function same (a: unknown, b: unknown): boolean {
  return JSON.stringify(a) === JSON.stringify(b)
}

function escAttr (s: string): string {
  return s.replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;').replace(/\{/g, '&lbrace;').replace(/\}/g, '&rbrace;').replace(/\n/g, '&#10;')
}

function escText (s: string): string {
  return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/\{/g, '&lbrace;').replace(/\}/g, '&rbrace;')
}

/** A cat or a lone expression as "text {hole} text" parts, or null when it has none of the literal text */
function templateParts (e: unknown): (string | { hole: unknown })[] | null {
  if (isObj(e) && Array.isArray(e.cat) && e.cat.some(p => typeof p === 'string')) {
    return e.cat.map(p => typeof p === 'string' ? p : { hole: p })
  }
  return null
}

function printTemplate (parts: (string | { hole: unknown })[], scope: NameScope, esc: (s: string) => string): string {
  return parts.map(p => typeof p === 'string' ? esc(p) : `{${printExpr(p.hole, scope)}}`).join('')
}

const ACTION_TAG_OF: Record<string, string> = Object.fromEntries(Object.entries(ACTION_TAGS).map(([tag, name]) => [name, tag]))

class Printer {
  private schema = markupSchema()
  private out: string[] = []

  line (depth: number, text: string): void {
    this.out.push(INDENT.repeat(depth) + text)
  }

  /** One attribute in its shortest form that compiles back to the value */
  attr (name: string, value: unknown, caps: Caps, scope: NameScope): string {
    const attrName = kebab(name)
    const candidates: string[] = []
    if (value === true && caps.boolean) candidates.push(attrName)
    if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') candidates.push(`${attrName}="${escAttr(String(value))}"`)
    if (isObj(value) && caps.responsive && Object.values(value).every(v => typeof v === 'number')) {
      candidates.push(`${attrName}="${Object.entries(value).map(([k, v]) => k === 'base' ? String(v) : `${k}:${v}`).join(' ')}"`)
    }
    if (Array.isArray(value) && caps.strings && value.every(v => typeof v === 'string' && !/[\s,]/.test(v))) candidates.push(`${attrName}="${escAttr(value.join(' '))}"`)
    const e = isObj(value) && caps.computed && 'expr' in value && Object.keys(value).length === 1 ? value.expr : caps.expr ? value : undefined
    if (e !== undefined) {
      const parts = templateParts(e)
      if (parts) candidates.push(`${attrName}="${printTemplate(parts, scope, escAttr)}"`)
      candidates.push(`${attrName}={${printExpr(e, scope)}}`)
    }
    for (const c of candidates) {
      if (same(compileAttribute(c, caps, scope), value)) return c
    }
    // Expressions read back with operators flattened, e.g. nested cats; the meaning is the same
    if (e !== undefined) return `${attrName}={${printExpr(e, scope)}}`
    return `${attrName}={{${JSON.stringify(value)}}}`
  }

  /** Text content that compiles back to the value, or null */
  content (value: unknown, scope: NameScope): string | null {
    const plain = (s: string) => s === s.trim() && !/ {2}|[\t\r]| \n|\n /.test(s) && !s.startsWith('\n') && !s.endsWith('\n')
    if (typeof value === 'string') {
      if (!value || !plain(value)) return null
      return value.split('\n').map(escText).join('<br/>')
    }
    if (isObj(value) && 'expr' in value && Object.keys(value).length === 1) {
      const parts = templateParts(value.expr)
      if (parts) {
        const literal = parts.filter((p): p is string => typeof p === 'string')
        if (literal.some(s => / {2}|[\t\r\n]/.test(s))) return null
        const first = parts[0]
        const last = parts[parts.length - 1]
        if ((typeof first === 'string' && first !== first.trimStart()) || (typeof last === 'string' && last !== last.trimEnd())) return null
        // Adjacent literals would merge when read back
        if (parts.some((p, i) => typeof p === 'string' && typeof parts[i + 1] === 'string')) return null
        return printTemplate(parts, scope, escText)
      }
      return `{${printExpr(value.expr, scope)}}`
    }
    return null
  }

  attrs (obj: Obj, props: Record<string, Caps>, scope: (prop: string) => NameScope, skip: Set<string>): string[] {
    const out: string[] = []
    for (const [k, v] of Object.entries(obj)) {
      if (skip.has(k) || v === undefined) continue
      const caps = props[k]
      if (!caps || caps.nodes || caps.actions || asChild(v, caps)) continue
      out.push(this.attr(k, v, caps, scope(k)))
    }
    return out
  }

  open (depth: number, tag: string, attrs: string[], selfClose: boolean): void {
    const one = `<${[tag, ...attrs].join(' ')}${selfClose ? '/>' : '>'}`
    if (INDENT.length * depth + one.length <= LINE || !attrs.length) { this.line(depth, one); return }
    this.line(depth, `<${tag}`)
    for (const a of attrs) this.line(depth + 2, a)
    this.line(depth + 1, selfClose ? '/>' : '>')
  }

  /** Child elements for object and array-of-object props, handlers, then nodes */
  childProps (obj: Obj, props: Record<string, Caps>, scope: (prop: string) => NameScope, depth: number, skip: Set<string> = new Set()): void {
    for (const [k, v] of Object.entries(obj)) {
      const caps = props[k]
      if (!caps || v === undefined || skip.has(k)) continue
      if (k === 'on' && isObj(v)) {
        for (const [event, list] of Object.entries(v)) {
          if (!Array.isArray(list)) continue
          this.line(depth, `<on:${event}>`)
          this.actions(list, scope('on'), depth + 1)
          this.line(depth, `</on:${event}>`)
        }
        continue
      }
      if (!asChild(v, caps)) continue
      if (caps.object && isObj(v)) this.object(kebab(k), v, caps.object, scope(k), depth)
      if (caps.objects && Array.isArray(v)) {
        for (const item of v) {
          if (!isObj(item)) continue
          const tag = k === 'options' && Array.isArray(item.options) ? 'optgroup' : singular(k)
          this.object(tag, item, caps.objects, scope(k), depth)
        }
      }
    }
  }

  object (tag: string, obj: Obj, props: Record<string, Caps>, scope: NameScope, depth: number): void {
    const textProp = TEXT_CONTENT[tag]
    const text = textProp && props[textProp] ? this.content(obj[textProp], scope) : null
    const skip = new Set<string>(text !== null && textProp ? [textProp] : [])
    const attrs = this.attrs(obj, props, () => scope, skip)
    const hasChildren = Object.entries(obj).some(([k, v]) => props[k] && v !== undefined && (k === 'on' || asChild(v, props[k]!)))
    if (!hasChildren && text === null) { this.open(depth, tag, attrs, true); return }
    if (!hasChildren && text !== null) {
      const one = `<${[tag, ...attrs].join(' ')}>${text}</${tag}>`
      if (INDENT.length * depth + one.length <= LINE) { this.line(depth, one); return }
    }
    this.open(depth, tag, attrs, false)
    if (text !== null) this.line(depth + 1, text)
    this.childProps(obj, props, () => scope, depth + 1)
    this.line(depth, `</${tag}>`)
  }

  node (node: Obj, scope: NameScope, depth: number): void {
    const type = String(node.type)
    const schema = this.schema.nodes[type]
    if (!schema) { this.line(depth, `<!-- unknown node ${escText(type)} -->`); return }
    const asName = typeof node.as === 'string' ? node.as : null
    const inner: NameScope = INNER_PROPS[type] && asName ? { ...scope, loops: [...scope.loops, asName] } : scope
    const scopeFor = (prop: string) => INNER_PROPS[type]?.includes(prop) ? inner : scope

    let tag = type
    const attrs: string[] = []
    const skip = new Set<string>(['type'])
    if (type === 'use') {
      const component = String(node.component ?? '')
      const pascal = snakeToPascal(component)
      if (pascal && pascalToSnake(pascal) === component) tag = pascal
      else attrs.push(this.attr('component', component, schema.props.component!, scope))
      skip.add('component').add('with')
      for (const [k, v] of Object.entries((node.with as Obj | undefined) ?? {})) {
        const c = typeof v === 'string' ? `${k}="${escAttr(v)}"` : `${k}={${printExpr(v, scope)}}`
        attrs.push(typeof v === 'string' && /\{|\}/.test(v) ? `${k}={${printExpr(v, scope)}}` : c)
      }
    }

    const textProp = TEXT_CONTENT[type]
    let text: string | null = null
    if (type === 'text' && Array.isArray(node.spans)) {
      text = this.spans(node.spans as Obj[], scope)
      if (text !== null) skip.add('spans')
    } else if (textProp && node[textProp] !== undefined) {
      text = this.content(node[textProp], scope)
      if (text !== null) skip.add(textProp)
    }
    attrs.push(...this.attrs(node, schema.props, scopeFor, skip))
    if (type === 'text' && Array.isArray(node.spans) && text === null) attrs.push(`spans={{${JSON.stringify(node.spans)}}}`)

    const children = Array.isArray(node.children) ? node.children as Obj[] : []
    const hasProps = Object.entries(node).some(([k, v]) => v !== undefined && !skip.has(k) && schema.props[k] && (k === 'on' || asChild(v, schema.props[k]!)))
    if (!children.length && !hasProps && text === null) {
      this.open(depth, tag, attrs, !(schema.props.children?.nodes && Array.isArray(node.children) && type !== 'list'))
      if (schema.props.children?.nodes && Array.isArray(node.children) && type !== 'list') this.line(depth, `</${tag}>`)
      return
    }
    if (!children.length && !hasProps && text !== null) {
      const one = `<${[tag, ...attrs].join(' ')}>${text}</${tag}>`
      if (INDENT.length * depth + one.length <= LINE) { this.line(depth, one); return }
    }
    this.open(depth, tag, attrs, false)
    if (text !== null) this.line(depth + 1, text)
    this.childProps(node, schema.props, scopeFor, depth + 1, skip)
    for (const child of children) this.node(child, scopeFor('children'), depth + 1)
    this.line(depth, `</${tag}>`)
  }

  spans (spans: Obj[], scope: NameScope): string | null {
    let out = ''
    for (const span of spans) {
      const keys = Object.keys(span).filter(k => k !== 'text')
      const text = typeof span.text === 'string'
        ? (span.text === '' ? null : escText(span.text).replace(/\n/g, '<br/>'))
        : isObj(span.text) && 'expr' in span.text
          ? (templateParts(span.text.expr) ? printTemplate(templateParts(span.text.expr)!, scope, escText) : `{${printExpr(span.text.expr, scope)}}`)
          : null
      if (text === null) return null
      if (!keys.length) { out += text; continue }
      const marks = Object.fromEntries(keys.map(k => [k, span[k]]))
      const tag = Object.entries(INLINE).find(([t, m]) => t !== 'span' && same(m, marks))?.[0]
      if (tag) { out += `<${tag}>${text}</${tag}>`; continue }
      const attrs = keys.map(k => span[k] === true ? kebab(k) : `${kebab(k)}="${escAttr(String(span[k]))}"`)
      out += `<span ${attrs.join(' ')}>${text}</span>`
    }
    // Leading or trailing spaces and doubled spaces would collapse when read back
    if (/^\s|\s$| {2}/.test(out.replace(/<[^>]+>/g, ''))) return null
    return out
  }

  actions (list: unknown[], scope: NameScope, depth: number): void {
    for (const raw of list) {
      if (!isObj(raw)) continue
      const name = String(raw.do)
      const tag = name === 'setIn' ? 'set' : ACTION_TAG_OF[name]
      const schema = this.schema.actions[name]
      if (!tag || !schema) { this.line(depth, `<!-- unknown action ${escText(name)} -->`); continue }
      const attrs: string[] = []
      const skip = new Set<string>(['do'])
      if (name === 'callOperation') {
        attrs.push(`op="${escAttr(String(raw.operation))}"`)
        skip.add('operation').add('input').add('then').add('catch')
        if (raw.into !== undefined) { attrs.push(this.attr('into', raw.into, schema.props.into!, scope)); skip.add('into') }
        for (const [k, v] of Object.entries((raw.input as Obj | undefined) ?? {})) {
          attrs.push(typeof v === 'string' && !/^-?\d/.test(v) && v !== 'true' && v !== 'false' ? `${k}="${escAttr(v)}"` : `${k}={${printExpr(v, scope)}}`)
        }
      }
      if (name === 'openConfirm') skip.add('then')
      const textProp = TEXT_CONTENT[tag]
      let text: string | null = null
      if (textProp && raw[textProp] !== undefined && name !== 'openConfirm') {
        text = this.content(raw[textProp], scope)
        if (text !== null) skip.add(textProp)
      }
      attrs.push(...this.attrs(raw, schema.props, () => scope, skip))
      const then = Array.isArray(raw.then) ? raw.then : null
      const catchList = Array.isArray(raw.catch) ? raw.catch : null
      const nested = name === 'callOperation' ? (then?.length || catchList?.length) : name === 'openConfirm' ? !!then?.length : false
      if (!nested && text === null) { this.open(depth, tag, attrs, true); continue }
      if (!nested && text !== null) {
        const one = `<${[tag, ...attrs].join(' ')}>${text}</${tag}>`
        if (INDENT.length * depth + one.length <= LINE) { this.line(depth, one); continue }
      }
      this.open(depth, tag, attrs, false)
      if (text !== null) this.line(depth + 1, text)
      if (name === 'openConfirm' && then) this.actions(then, scope, depth + 1)
      if (name === 'callOperation') {
        if (then?.length) { this.line(depth + 1, '<then>'); this.actions(then, scope, depth + 2); this.line(depth + 1, '</then>') }
        if (catchList?.length) { this.line(depth + 1, '<catch>'); this.actions(catchList, scope, depth + 2); this.line(depth + 1, '</catch>') }
      }
      this.line(depth, `</${tag}>`)
    }
  }

  spec (spec: MaxInterfaceSpec): string {
    const root = this.schema.root
    const scope: NameScope = { loops: [], params: [], derived: Object.keys(spec.derive ?? {}), state: Object.keys(spec.state ?? {}) }
    const attrs: string[] = []
    for (const key of ['title', 'description', 'locale', 'preset', 'capabilities', 'metadataNamespaces', 'metadataRead', 'refresh'] as const) {
      const v = spec[key]
      if (v === undefined) continue
      const a = this.attr(key, v, root[key]!, scope)
      attrs.push(key === 'metadataNamespaces' ? a.replace(/^metadata-namespaces/, 'metadata') : a)
    }
    this.open(0, 'tool', attrs, false)
    for (const [name, def] of Object.entries(spec.state ?? {})) {
      const parts = [`name="${escAttr(name)}"`, `type="${def.type}"`]
      if (def.initial !== undefined) {
        const i = def.initial
        parts.push(typeof i === 'string' ? `initial="${escAttr(i)}"` : (i === null || typeof i !== 'object') ? `initial={${JSON.stringify(i)}}` : `initial={{${JSON.stringify(i)}}}`)
      }
      this.line(1, `<state ${parts.join(' ')}/>`)
    }
    for (const [name, ref] of Object.entries(spec.operations ?? {})) {
      const parts = [`name="${escAttr(name)}"`, `op="${escAttr(ref.operation)}"`]
      if (ref.label !== undefined) parts.push(`label="${escAttr(ref.label)}"`)
      if (ref.confirmAbove !== undefined) parts.push(`confirm-above="${ref.confirmAbove}"`)
      this.open(1, 'operation', parts, true)
    }
    for (const [name, e] of Object.entries(spec.derive ?? {})) {
      this.open(1, 'derive', [`name="${escAttr(name)}"`, `value={${printExpr(e, scope)}}`], true)
    }
    for (const [name, def] of Object.entries(spec.components ?? {})) {
      const pascal = snakeToPascal(name)
      const shown = pascal && pascalToSnake(pascal) === name ? pascal : name
      const parts = [`name="${escAttr(shown)}"`]
      if (def.params?.length) parts.push(`params="${def.params.join(' ')}"`)
      this.line(1, `<component ${parts.join(' ')}>`)
      const inner: NameScope = { ...scope, params: def.params ?? [] }
      for (const n of def.body ?? []) this.node(n, inner, 2)
      this.line(1, '</component>')
    }
    for (const [name, def] of Object.entries(spec.icons ?? {})) {
      const parts = [`name="${escAttr(name)}"`]
      if (def.viewBox) parts.push(`view-box="${escAttr(def.viewBox)}"`)
      if (def.style) parts.push(`style="${def.style}"`)
      parts.push(`path="${escAttr(def.path)}"`)
      this.open(1, 'icon-def', parts, true)
    }
    for (const [name, def] of Object.entries(spec.panels ?? {})) {
      const parts = [`name="${escAttr(name)}"`, this.attr('title', def.title, { ...this.schema.root.title!, computed: true }, scope)]
      if (def.size) parts.push(`size="${def.size}"`)
      this.open(1, 'panel', parts, false)
      for (const n of def.body ?? []) this.node(n, scope, 2)
      this.line(1, '</panel>')
    }
    if (spec.on?.load?.length) {
      this.line(1, '<on:load>')
      this.actions(spec.on.load, scope, 2)
      this.line(1, '</on:load>')
    }
    for (const n of spec.body ?? []) this.node(n, scope, 1)
    this.line(0, '</tool>')
    return this.out.join('\n') + '\n'
  }
}

export function printMarkup (spec: MaxInterfaceSpec): string {
  return new Printer().spec(spec)
}

